import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { FileText, Save, Send, CalendarIcon, ArrowLeft, MoreHorizontal, X, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { WorkflowSidebar } from "@/components/policy/WorkflowSidebar";
import { TranslationButton } from "@/components/policy/TranslationButton";
import { PolicyCover } from "@/components/policy/PolicyCover";
import { PolicyRichTextEditor } from "@/components/policy/PolicyRichTextEditor";
import { StatusBadge } from "@/components/ui/status-badge";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import logo from "@/assets/logo.png";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link } from "react-router-dom";
import { ActivityLogger } from "@/lib/activity-logger";
import { fetchDepartments, Department } from "@/lib/department-utils";
import { isRtlText } from "@/lib/text-direction";
import { formatFramework, useCompliancePrograms } from "@/hooks/use-compliance-programs";

interface PolicyEditorProps {
  policyId?: string; // If provided, we're editing an existing policy
  onSave?: (policyId: string) => void; // Callback after successful save
  onCancel?: () => void; // Callback for cancel action
}

interface PolicyData {
  id?: string;
  title: string;
  department: string;
  content: string;
  version: number;
  effectiveDate?: Date;
  nextRenewalDate?: Date;
  assignedReviewers: string[];
  assignedApprovers: string[];
  status: 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected';
  programId: string | null;
}

const PolicyEditor = ({ policyId, onSave, onCancel }: PolicyEditorProps) => {
  const [formData, setFormData] = useState<PolicyData>({
    title: "",
    department: "",
    content: "",
    version: 1,
    assignedReviewers: [],
    assignedApprovers: [],
    status: "draft" as const,
    programId: null,
  });
  
  const [effectiveDate, setEffectiveDate] = useState<Date>();
  const [nextRenewalDate, setNextRenewalDate] = useState<Date>();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [reviewerDetails, setReviewerDetails] = useState<{ id: string; name: string; designation?: string }[]>([]);
  const [approverDetails, setApproverDetails] = useState<{ id: string; name: string; designation?: string }[]>([]);
  const [linkedControls, setLinkedControls] = useState<any[]>([]);
  const [directionOverride, setDirectionOverride] = useState<"ltr" | "rtl" | null>(null);
  const detectedRtl = isRtlText(formData.content);
  const effectiveDirection = directionOverride ?? (detectedRtl ? "rtl" : "ltr");
  
  const { toast } = useToast();
  const { profile, hasAnyRole, company } = useAuth();
  const { programs } = useCompliancePrograms();
  const navigate = useNavigate();
  
  const [policyCreatedBy, setPolicyCreatedBy] = useState<string | null>(null);
  const [canEditPolicy, setCanEditPolicy] = useState(true);
  const [originalProgramId, setOriginalProgramId] = useState<string | null>(null);

  // Load existing policy data if editing
  useEffect(() => {
    if (policyId) {
      loadPolicyData();
    }
  }, [policyId]);

  // Load departments
  useEffect(() => {
    if (profile?.company_id) {
      fetchDepartments(profile.company_id)
        .then(depts => {
          setDepartments(depts);
          // Set default department to "General" if creating new policy
          if (!policyId && depts.length > 0 && !formData.department) {
            const generalDept = depts.find(d => d.name === 'General');
            if (generalDept) {
              setFormData(prev => ({ ...prev, department: generalDept.name }));
            }
          }
        })
        .catch(console.error);
    }
  }, [profile?.company_id]);

  // Load user details for reviewers and approvers
  const loadUserDetails = async (userIds: string[]) => {
    if (userIds.length === 0) return [];
    
    try {
      const { data: users, error } = await supabase
        .from('profiles')
        .select('id, name, designation')
        .in('id', userIds);
      
      if (error) throw error;
      return users || [];
    } catch (error) {
      console.error('Error loading user details:', error);
      return [];
    }
  };

  // Update user details when assigned reviewers/approvers change
  useEffect(() => {
    const loadDetails = async () => {
      const [reviewers, approvers] = await Promise.all([
        loadUserDetails(formData.assignedReviewers),
        loadUserDetails(formData.assignedApprovers)
      ]);
      setReviewerDetails(reviewers);
      setApproverDetails(approvers);
    };
    
    loadDetails();
  }, [formData.assignedReviewers, formData.assignedApprovers]);

  const loadPolicyData = async () => {
    if (!policyId) return;
    
    setLoading(true);
    try {
      const { data: policy, error } = await (supabase as any)
        .from('policies')
        .select('*')
        .eq('id', policyId)
        .single();

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load policy data.",
          variant: "destructive",
        });
        return;
      }

      if (policy) {
        // Check if user can edit this policy
        const isCreator = policy.created_by === profile?.id;
        const isAdmin = hasAnyRole(['admin']);
        const canEdit = isCreator || isAdmin;
        
        setPolicyCreatedBy(policy.created_by);
        setCanEditPolicy(canEdit);
        setOriginalProgramId(policy.program_id || null);
        
        setFormData({
          id: policy.id,
          title: policy.title || "",
          department: policy.department || "general",
          content: policy.content || "",
          version: policy.version || 1,
          assignedReviewers: policy.assigned_reviewers || [],
          assignedApprovers: policy.assigned_approvers || [],
          status: policy.status || "draft",
          programId: policy.program_id || null,
        });

        const { data: controlData } = await (supabase as any)
          .from('policy_control_mappings')
          .select('id, program_id, framework_control:framework_controls(framework, control_id, title, domain)')
          .eq('policy_id', policyId);

        setLinkedControls(controlData || []);

        if (policy.effective_date) {
          setEffectiveDate(new Date(policy.effective_date));
        }
        if (policy.next_renewal_date) {
          setNextRenewalDate(new Date(policy.next_renewal_date));
        }
      }
    } catch (error) {
      console.error('Error loading policy:', error);
      toast({
        title: "Error",
        description: "Failed to load policy data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.department || !formData.content || !profile?.company_id) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const policyData = {
        title: formData.title,
        department: formData.department,
        content: formData.content,
        version: formData.version,
        effective_date: effectiveDate?.toISOString().split('T')[0] || null,
        next_renewal_date: nextRenewalDate?.toISOString().split('T')[0] || null,
        assigned_reviewers: formData.assignedReviewers,
        assigned_approvers: formData.assignedApprovers,
        program_id: formData.programId || null,
        company_id: profile.company_id,
        created_by: profile.id,
        status: formData.status as 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected'
      } as const;

      let result;
      if (policyId) {
        // Update existing policy
        const { data, error } = await (supabase as any)
          .from('policies')
          .update(policyData)
          .eq('id', policyId)
          .select('id')
          .single();
        
        if (error) throw error;
        result = data;
      } else {
        // Create new policy
        const { data, error } = await (supabase as any)
          .from('policies')
          .insert(policyData)
          .select('id')
          .single();
        
        if (error) throw error;
        result = data;
      }

      if (policyId && originalProgramId !== formData.programId) {
        await (supabase as any)
          .from('policy_control_mappings')
          .delete()
          .eq('policy_id', policyId);
      }

      // Log activity
      if (policyId) {
        await ActivityLogger.logPolicyEdited(
          profile.company_id,
          profile.id,
          result.id,
          formData.title
        );
      } else {
        await ActivityLogger.logPolicyCreated(
          profile.company_id,
          profile.id,
          result.id,
          formData.title
        );
      }

      toast({
        title: "Success",
        description: policyId ? "Policy updated successfully!" : "Policy created successfully!",
      });

      if (onSave) {
        onSave(result.id);
      } else {
        navigate(`/policy/${result.id}`);
      }
    } catch (error: any) {
      console.error('Error saving policy:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save policy.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!formData.title || !formData.department || !formData.content || !profile?.company_id) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const policyData = {
        title: formData.title,
        department: formData.department,
        content: formData.content,
        version: formData.version,
        effective_date: effectiveDate?.toISOString().split('T')[0] || null,
        next_renewal_date: nextRenewalDate?.toISOString().split('T')[0] || null,
        assigned_reviewers: formData.assignedReviewers,
        assigned_approvers: formData.assignedApprovers,
        program_id: formData.programId || null,
        company_id: profile.company_id,
        created_by: profile.id,
        status: 'under_review' as const
      };

      let result;
      if (policyId) {
        const { data, error } = await (supabase as any)
          .from('policies')
          .update(policyData)
          .eq('id', policyId)
          .select('id')
          .single();
        
        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await (supabase as any)
          .from('policies')
          .insert(policyData)
          .select('id')
          .single();
        
        if (error) throw error;
        result = data;
      }

      if (policyId && originalProgramId !== formData.programId) {
        await (supabase as any)
          .from('policy_control_mappings')
          .delete()
          .eq('policy_id', policyId);
      }

      // Create workflow entry
      await supabase
        .from('policy_workflow')
        .insert({
          policy_id: result.id,
          status: 'under_review',
          assigned_to: formData.assignedReviewers[0] || null,
          action: 'submitted',
          previous_status: policyId ? formData.status : 'draft',
          new_status: 'under_review',
          created_by: profile.id,
          company_id: profile.company_id
        });

      // Log activity
      await ActivityLogger.logPolicyStatusChanged(
        profile.company_id,
        profile.id,
        result.id,
        formData.title,
        policyId ? formData.status : 'draft',
        'under_review'
      );

      toast({
        title: "Success",
        description: "Policy submitted for review!",
      });

      if (onSave) {
        onSave(result.id);
      } else {
        navigate(`/policy/${result.id}`);
      }
    } catch (error: any) {
      console.error('Error submitting policy:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit policy for review.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading policy data...</p>
        </div>
      </div>
    );
  }

  const selectedProgram = programs.find((program) => program.id === formData.programId);

  // Show permission denied UI if user cannot edit this policy
  if (policyId && !canEditPolicy) {
    return (
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-destructive">Permission Denied</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              You do not have permission to edit this policy. Only the creator or an admin can edit this policy.
            </p>
            <Button 
              variant="outline" 
              onClick={() => window.history.back()}
            >
              Back to Policy
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        {policyId ? (
          <Link to={`/policy/${policyId}`}>
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Back to Policy
            </Button>
          </Link>
        ) : <div />}

        <div className="flex items-center gap-2">
          {/* Secondary actions overflow */}
          {(onCancel || (policyId && formData.title && formData.content && profile?.company_id && profile?.id)) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {policyId && formData.title && formData.content && profile?.company_id && profile?.id && (
                  <DropdownMenuItem asChild>
                    <div className="w-full">
                      <TranslationButton
                        policyId={policyId}
                        title={formData.title}
                        content={formData.content}
                        department={formData.department}
                        version={formData.version}
                        effectiveDate={effectiveDate?.toISOString().split('T')[0] || null}
                        nextRenewalDate={nextRenewalDate?.toISOString().split('T')[0] || null}
                        assignedReviewers={formData.assignedReviewers}
                        assignedApprovers={formData.assignedApprovers}
                        companyId={profile.company_id}
                        userId={profile.id}
                      />
                    </div>
                  </DropdownMenuItem>
                )}
                {onCancel && (
                  <DropdownMenuItem onClick={onCancel}>
                    <X className="w-4 h-4 mr-2" />Cancel
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Saving…' : 'Save Draft'}
          </Button>
          <Button size="sm" onClick={handleSubmitForReview} disabled={saving}>
            <Send className="w-4 h-4 mr-1.5" />
            {saving ? 'Submitting…' : 'Submit for Review'}
          </Button>
        </div>
      </div>

      {/* Cover + document header card */}
      <Card className="shadow-card overflow-hidden">
        <PolicyCover
          coverUrl={company?.cover_image_url}
          companyName={company?.name ?? ''}
          className="w-full h-40"
        />
        <CardContent className="pt-4 pb-5">
          <div className="flex items-start gap-4">
            <img src={logo} alt="Company Logo" className="w-10 h-10 object-contain shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Untitled Policy"
                  className="text-2xl font-semibold bg-transparent border-0 outline-none w-full placeholder:text-muted-foreground/40 focus:ring-0 p-0 text-foreground"
                />
                <StatusBadge status="draft" className="shrink-0">Draft</StatusBadge>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {formData.department || 'No department set'} · v{formData.version}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedProgram && (
        <Card className="shadow-card">
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-md bg-primary/10 p-2 text-primary">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-medium">Compliance program</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFramework(selectedProgram.framework)} · {selectedProgram.name}
                  </p>
                </div>
              </div>
              <div className="flex max-w-full flex-wrap gap-2 sm:justify-end">
                {linkedControls.length > 0 ? (
                  <>
                    {linkedControls.slice(0, 8).map((mapping) => (
                      <Badge key={mapping.id} variant="outline">
                        {mapping.framework_control?.framework} {mapping.framework_control?.control_id}
                      </Badge>
                    ))}
                    {linkedControls.length > 8 && <Badge variant="secondary">+{linkedControls.length - 8}</Badge>}
                  </>
                ) : (
                  <Badge variant="outline">Program linked</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-6 space-y-6">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Policy Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Department */}
              <div className="space-y-2">
                <Label htmlFor="department">Department *</Label>
                <Select value={formData.department} onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.name}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>


              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="content">Policy Content *</Label>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Direction:</span>
                    <Button
                      type="button"
                      variant={directionOverride === "ltr" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setDirectionOverride("ltr")}
                      className="h-7 px-3"
                    >
                      LTR
                    </Button>
                    <Button
                      type="button"
                      variant={directionOverride === "rtl" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setDirectionOverride("rtl")}
                      className="h-7 px-3"
                    >
                      RTL
                    </Button>
                    <Button
                      type="button"
                      variant={directionOverride === null ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setDirectionOverride(null)}
                      className="h-7 px-3"
                    >
                      Auto
                    </Button>
                    {directionOverride === null && (
                      <span className="text-[11px] text-muted-foreground">
                        {detectedRtl ? "Detected RTL" : "Detected LTR"}
                      </span>
                    )}
                  </div>
                </div>
                <PolicyRichTextEditor
                  direction={effectiveDirection}
                  value={formData.content}
                  onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                  placeholder="Enter the detailed policy content..."
                  policyContext={{
                    title: formData.title,
                    department: formData.department,
                    companyName: company?.name ?? undefined,
                    programName: selectedProgram?.name,
                    framework: selectedProgram?.framework ? formatFramework(selectedProgram.framework) : undefined,
                    linkedControls: linkedControls.map((mapping) =>
                      [
                        mapping.framework_control?.framework,
                        mapping.framework_control?.control_id,
                        mapping.framework_control?.title,
                      ]
                        .filter(Boolean)
                        .join(" "),
                    ),
                  }}
                />
              </div>

            </CardContent>
          </Card>
        </div>


        {/* Workflow Sidebar */}
        <div className="lg:col-span-4 space-y-4">
          {/* Policy Information */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Policy Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  type="number"
                  value={formData.version}
                  onChange={(e) => setFormData(prev => ({ ...prev, version: parseInt(e.target.value) || 1 }))}
                  min="1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="program">Compliance Program</Label>
                <Select
                  value={formData.programId || "none"}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, programId: value === "none" ? null : value }))}
                >
                  <SelectTrigger id="program">
                    <SelectValue placeholder="Map to a program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No program</SelectItem>
                    {programs.map((program) => (
                      <SelectItem key={program.id} value={program.id}>
                        {formatFramework(program.framework)} · {program.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="effectiveDate">Effective Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !effectiveDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {effectiveDate ? format(effectiveDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={effectiveDate}
                      onSelect={setEffectiveDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nextRenewalDate">Next Renewal Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !nextRenewalDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {nextRenewalDate ? format(nextRenewalDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={nextRenewalDate}
                      onSelect={setNextRenewalDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          {/* Workflow Assignment and Visualization */}
          <WorkflowSidebar
            currentStatus={formData.status}
            reviewers={reviewerDetails}
            approvers={approverDetails}
            creator={profile ? {
              name: profile.name || 'Unknown User',
              designation: profile.designation || 'Creator'
            } : null}
            onReviewersChange={(reviewerIds) => setFormData(prev => ({ ...prev, assignedReviewers: reviewerIds }))}
            onApproversChange={(approverIds) => setFormData(prev => ({ ...prev, assignedApprovers: approverIds }))}
            companyId={profile?.company_id}
            showWorkflow={false}
          />
        </div>
      </div>
    </div>
  );
};

export default PolicyEditor;
